import { useCustomToast } from '@elkaso-app/web-design';
import http from 'apis/config/http-service';
import { useMutationWrapper } from 'apis/config/use-api-wrapper';
import type { AxiosError } from 'axios';
import { UseMutationOptions, useQueryClient } from 'react-query';
import type { ProductBrand } from './types';
import { productBrandsEndpoint } from './use-get-product-brands-api';

export interface CreateProductBrandPayload {
  name: string;
  isActive?: boolean;
}

type TData = ProductBrand;
type TError = AxiosError;
type TVariables = CreateProductBrandPayload;

type Options = Omit<UseMutationOptions<TData, TError, TVariables>, 'mutationFn'>;
type TReturnType = ReturnType<typeof useMutationWrapper<TData, TError, TVariables>>;

// POST ums/v1/cms/product-brands
export function useCreateProductBrandApi(options?: Options): TReturnType {
  const queryClient = useQueryClient();
  const toast = useCustomToast();
  const { onSuccess: userOnSuccess, ...restOptions } = options || {};

  const mutationFn = async (variables: TVariables): Promise<TData> => {
    try {
      // The API wraps the resource in an envelope: { data: {...} }
      const response = await http.post(productBrandsEndpoint, variables);
      return response.data.data;
    } catch (error: any) {
      if (!error.response?.status || !error.response?.data?.message) throw error;

      toast({
        description: error.response.data.message,
        status: 'error',
      });

      throw error;
    }
  };

  return useMutationWrapper(mutationFn, {
    mutationKey: [productBrandsEndpoint],
    onSuccess: (data, variables, context) => {
      // Fuzzy-matches both the admin list ([endpoint]) and the dropdown
      // options ([endpoint, { isActive: true }]).
      queryClient.invalidateQueries([productBrandsEndpoint]);
      queryClient.invalidateQueries([`${productBrandsEndpoint}/usage`]);
      userOnSuccess?.(data, variables, context);
    },
    ...restOptions,
  });
}
